import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress
} from "@mui/material";
import { IoPersonAddOutline, IoCheckmark, IoArrowBack } from "react-icons/io5";
import { School as SchoolIcon } from '@mui/icons-material';
import config from "../config";
import { adminColors } from "../theme/adminTheme";
import { FONT_STACK, BORDER_COLOR } from "../theme/uiConstants";

// Shared focus treatment for form fields — indigo ring/label, no default MUI asterisk.
// Kept identical to Signin.js's fieldFocusSx so the two pages read as one flow.
const fieldFocusSx = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: adminColors.accent,
    borderWidth: "1.5px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: adminColors.accent,
  },
  "& .MuiFormLabel-asterisk": {
    display: "none",
  },
};

export default function RequestAccess() {
  const navigate = useNavigate();
  const location = useLocation();

  // Signin.js hands off `blocked: true` whenever a 403 (account exists but
  // has no access) sent someone here, plus a best-effort prefilled email
  // when the typed username happened to look like one. These are
  // independent: a blocked user with a non-email username still gets the
  // "no access yet" framing below, just without the prefill.
  const prefillEmail = location.state?.email || "";
  const fromBlockedAccount = Boolean(location.state?.blocked);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(prefillEmail);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [confirmationNote, setConfirmationNote] = useState("");
  const [mounted, setMounted] = useState(false);

  const nameRef = useRef(null);
  const confirmationHeadingRef = useRef(null);

  useEffect(() => {
    setMounted(true);
    if (!prefillEmail) {
      nameRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move focus to the confirmation so screen reader users get the outcome
  // announced instead of landing on a page that silently changed under them.
  useEffect(() => {
    if (submitted) {
      confirmationHeadingRef.current?.focus();
    }
  }, [submitted]);

  // Reused verbatim from the old inline contact form in Signin.js — same
  // endpoint, same payload shape, same validation and error handling, so
  // the backend and the admin "Sign-up Requests" view keep working unchanged.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const res = await fetch(`${config.API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message }),
      });

      if (res.ok) {
        const data = await res.json();
        // Surface any server-provided detail (e.g. next steps, a contact
        // name) as a secondary line under the designed confirmation state
        // below. We deliberately don't promise a specific turnaround time
        // in the copy above — that's not something this codebase or the
        // backend response commits to, so only echo what the API sends.
        setConfirmationNote(data.msg || "");
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || "Could not send your request. Please try again.");
      }
    } catch (error) {
      console.error("Contact request failed:", error?.message ?? error);
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!mounted) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: adminColors.surface
      }}>
        <CircularProgress sx={{ color: adminColors.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: adminColors.surface,
        display: "flex",
        flexDirection: "column",
        fontFamily: FONT_STACK,
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            transitionDuration: "0.01ms !important",
            animationDuration: "0.01ms !important",
          },
        },
      }}
    >
      {/* Navigation Header — identical lockup to Signin.js so the two pages read as one flow */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          px: 3,
          py: 2,
          backgroundColor: "white",
          borderBottom: `1px solid ${BORDER_COLOR}`
        }}
      >
        <Link
          onClick={() => navigate("/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            textDecoration: "none",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: adminColors.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SchoolIcon sx={{ color: "white", fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 17, color: adminColors.ink, letterSpacing: "-0.01em" }}>
            LearnAndMasterAI
          </Typography>
        </Link>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 440,
            p: 4,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: "12px"
          }}
        >
          {submitted ? (
            // Designed confirmation state — replaces the old silent submit.
            // Tells the visitor what happens next and when, plus a way back.
            <Box
              sx={{
                textAlign: "center",
                animation: "requestAccessConfirmIn 320ms cubic-bezier(0.23, 1, 0.32, 1)",
                "@keyframes requestAccessConfirmIn": {
                  from: { opacity: 0, transform: "translateY(8px) scale(0.98)" },
                  to: { opacity: 1, transform: "translateY(0) scale(1)" },
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: adminColors.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3
                }}
              >
                <IoCheckmark size={24} color="white" />
              </Box>

              <Typography
                ref={confirmationHeadingRef}
                tabIndex={-1}
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: adminColors.ink,
                  mb: 1.5,
                  fontSize: { xs: '1.4rem', sm: '1.75rem' },
                  lineHeight: 1.2,
                  outline: "none"
                }}
              >
                Request received
              </Typography>

              <Typography variant="body1" sx={{ color: adminColors.inkSoft, lineHeight: 1.6, mb: confirmationNote ? 1 : 4 }}>
                A school administrator will review your request and get back to you at{" "}
                <Box component="span" sx={{ color: adminColors.ink, fontWeight: 600 }}>{email}</Box>.
              </Typography>

              {confirmationNote && (
                <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: 4 }}>
                  {confirmationNote}
                </Typography>
              )}

              <Button
                variant="outlined"
                onClick={() => navigate("/signin")}
                startIcon={<IoArrowBack size={16} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  borderColor: BORDER_COLOR,
                  color: adminColors.ink,
                  "&:hover": {
                    borderColor: adminColors.accent,
                    backgroundColor: adminColors.accentLight
                  }
                }}
              >
                Back to sign in
              </Button>
            </Box>
          ) : (
            <>
              {/* Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: adminColors.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3
                  }}
                >
                  <IoPersonAddOutline size={22} color="white" />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: adminColors.ink,
                    mb: 1,
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    lineHeight: 1.2
                  }}
                >
                  Request access
                </Typography>

                <Typography variant="body2" sx={{ color: adminColors.inkSoft, lineHeight: 1.6 }}>
                  {fromBlockedAccount
                    ? "Your account doesn't have access yet. Send us your details and we'll get back to you."
                    : "Tell us a bit about your school and we'll get back to you about access."}
                </Typography>
              </Box>

              {/* Error Message */}
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3, "& .MuiAlert-message": { fontSize: "14px" } }}
                >
                  {error}
                </Alert>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Your name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  ref={nameRef}
                  sx={{ mb: 2.5, ...fieldFocusSx }}
                  disabled={submitting}
                />

                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{ mb: 2.5, ...fieldFocusSx }}
                  disabled={submitting}
                />

                <TextField
                  fullWidth
                  label="Message (optional)"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  multiline
                  minRows={3}
                  sx={{ mb: 3.5, ...fieldFocusSx }}
                  disabled={submitting}
                />

                {/* Notice sits before the submit button, both in visual scan
                    order and keyboard tab order. The link opens in a new tab
                    rather than navigating away, since a normal click here
                    would otherwise discard whatever's already been typed
                    into this form. */}
                <Typography
                  variant="caption"
                  sx={{ display: "block", textAlign: "center", color: adminColors.inkSoft, mb: 1.5, lineHeight: 1.5 }}
                >
                  We use this information only to respond to your request — see our{" "}
                  <Box
                    component="a"
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: adminColors.accent, fontWeight: 600 }}
                  >
                    Privacy Policy
                  </Box>
                  .
                </Typography>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={submitting || !email}
                  sx={{
                    mb: 3.5,
                    py: 1.5,
                    backgroundColor: adminColors.accent,
                    "&:hover": {
                      backgroundColor: adminColors.accentDark
                    },
                    "&.Mui-disabled": {
                      backgroundColor: adminColors.accentBorder,
                      color: adminColors.accentDark
                    },
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 15,
                    borderRadius: "8px",
                    transition: "transform 120ms ease-out, background-color 150ms ease",
                    "&:active": {
                      transform: submitting ? "none" : "scale(0.98)"
                    }
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={20} sx={{ color: adminColors.accentDark }} />
                  ) : (
                    "Send request"
                  )}
                </Button>
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
                  Already have access?{" "}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate("/signin")}
                    sx={{
                      fontWeight: 600,
                      color: adminColors.accent,
                      cursor: "pointer",
                      verticalAlign: "baseline",
                      "&:hover": { color: adminColors.accentDark }
                    }}
                  >
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
}
